import i18next from "@/i18n";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import useValidateDiscountCode from "@repo/shopify/hooks/useValidateDiscountCode";
import { storeQueue } from "@repo/shopify/src/api/storeQueue";
import { cartBuyerIdentityUpdateMutation, cartQuery } from "@repo/shopify/src/queries/cart-gql";
import {
  adjustCartLines,
  extractId,
  getQuantityLimitObj,
  linesEqual,
  linesEqualProduct,
} from "@repo/shopify/src/utils";
import { transformWon } from "@repo/utils/src/number";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@components/atoms/Button";
import Loading from "@components/molecules/Loading";

import { useCheckoutContext } from "@organisms/Checkout/CheckoutContext";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";
import { getValidOrders } from "@utils/order";

function Payment({ refetch, discountLoading }) {
  const { openModal, openToast, closeModal } = useLayout();
  const { payment, paymentParameter, selectedAddress, requestPayment, isProgressing, postPayment, paymentMethod } =
    useCheckoutContext();
  const { validateDiscountCode } = useValidateDiscountCode();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /** 개수제한을 못 맞춰 카트 새로 생성 후 capacity가 0인 상품들만 남았을 경우 */
  const [isEmpty, setIsEmpty] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");
  const amount = searchParams.get("amount");
  const errorMessage = searchParams.get("message");
  const brandPay = searchParams.get("paymentType");

  const { extractCart } = useCheckoutContext();
  const { cart, updateCart, getCartLineItem, updateCartLineBase, createCartWithParams } = useCart();
  const { accessToken, customer, updateUser, fetchUser, userLogout } = useUser();

  const [setBuyerIdentity] = useMutation(gql(cartBuyerIdentityUpdateMutation.query), {
    onCompleted: async (data) => {
      await refetch();
    },
    onError: (error) => {
      openModal({
        title: "Payment Error",
        description: NOTIFICATIONS.ERROR_PAYMENT_SET.message,
        confirmButton: {
          label: "Okay",
          onClick: () => {
            closeModal();
          },
        },
      });

      datadogLogs.logger.error("[SHOPIFY][CartBuyerIdentityUpdate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CartBuyerIdentityUpdate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  const [checkCheckout] = useLazyQuery(gql(cartQuery.query), {
    variables: { id: cart.id },
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      // cart에 buyerIdentity가 없을 때의 fallback logic
      if (!data?.cart?.buyerIdentity?.customer) {
        await setBuyerIdentity({ variables: { cartId: cart.id, buyerIdentity: { customerAccessToken: accessToken } } });
      }
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Cart] Failed to query", error);
      throw new Error(`[SHOPIFY][Cart] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  const { t } = useTranslation();

  // 특정 주소지 막기
  const blockedAddressList = useRef([
    { zip: "05842", roadname_address: "송파구 송파대로 55", lotnumber_address: "서울 송파구 장지동 875" }, // 크림
    { zip: "07983", roadname_address: "양천구 목동동로 363", lotnumber_address: "서울 양천구 목동 906" }, // 솔드아웃
  ]);

  /** 입력된 코드가 멤버십 할인코드인지 확인*/
  const isCheckMembershipCode = (discountCode) => {
    // WELCOME 코드인지 판단
    if (discountCode.toLowerCase() === "welcome") {
      return true;
    }

    const month = (new Date().getMonth() + 1).toString().padStart(2, "0");
    const membershipDiscountCode = `membership${month}`;

    return discountCode.toLowerCase().includes(membershipDiscountCode);
  };

  useEffect(() => {
    /** 토스 페이먼츠 requestPayment 실패로 진입 */
    if (errorMessage) {
      console.log(`[TUNE] Error ErrorMessage : ${errorMessage}, Error Message : ${errorMessage}`);

      openModal({
        title: "Payment Error",
        description: errorMessage,
        confirmButton: {
          label: "Okay",
          onClick() {
            setSearchParams({});
            closeModal();
          },
        },
      });

      refetch();
      return;
    }

    console.log("[TUNE] ORDER SESSION STORAGE", paymentParameter);

    /** 토스 페이먼츠 requestPayment 성공으로 진입 */
    if (!paymentKey || !orderId || !amount) return;

    let intervalId = null;

    // 카트 유효한 재고 확인
    void getCartLineItem({ variables: { id: cart.id } }).then((res) => {
      if (!res?.data?.cart) {
        openModal({
          title: "Payment Error",
          description: NOTIFICATIONS.ERROR_PAYMENT_COMPLETE.message,
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });

        return;
      }

      const updatedCart = res.data.cart;
      const quantityAdjustedCartLines = updatedCart.lines.edges.map(({ node }) => ({
        id: node.id,
        quantity:
          !node.merchandise.availableForSale || Number(node.quantity) > Number(node.merchandise.quantityAvailable)
            ? Number(node.merchandise.quantityAvailable)
            : Number(node.quantity),
      }));

      const isQuantityInsufficient = quantityAdjustedCartLines?.some(
        (line, index) => line.quantity !== Number(updatedCart.lines.edges[index].node.quantity),
      );

      if (isQuantityInsufficient) {
        openModal({
          title: "Alert",
          description:
            "주문하시려는 상품의 수량이 주문 과정에서 구매 가능 수량을 초과하였습니다. 장바구니에서 해당 상품의 수량이 구매 가능 수량에 맞게 조정되며 품절된 상품은 제거됩니다.",
          confirmButton: {
            label: "Okay",
            onClick() {
              void updateCartLineBase({
                variables: { cartId: cart.id, lines: quantityAdjustedCartLines },
              }).then(() => {
                if (!quantityAdjustedCartLines.some((node) => node.quantity > 0)) setIsEmpty(true);
                closeModal();
              });
            },
          },
        });

        return;
      }

      // 대기열에 등록 및 조회 - 주기적으로 실행 (1초마다)
      intervalId = setInterval(() => {
        void applyStoreQueue();
      }, 1000);

      postPayment({ cartId: cart.id, paymentKey, orderId, amount, brandPay });
    });

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // 대기열 등록
  const applyStoreQueue = async () => {
    if (!customer?.id) return;
    const result = await storeQueue({ shop: "tune", customerId: extractId(customer.id), cartId: extractId(cart?.id) });
    if (result.rank) {
      console.log(result.rank);
    }
  };

  useEffect(() => {
    if (isEmpty && cart.lines.edges.length === 0) {
      openModal({
        title: "ALERT",
        description: "카트에 상품이 존재하지 않습니다.",
        confirmButton: {
          label: "Okay",
          onClick() {
            navigate(URLS.HOME);
            closeModal();
          },
        },
      });
    }
  }, [isEmpty, cart]);

  // 주소에 모든 필수 키에 대한 값이 있는지 확인
  const isValidAddress = (address) => {
    if (!selectedAddress) return;
    const requiredKeys = ["firstName", "lastName", "phone", "zip", "address1", "address2", "province"];
    return requiredKeys.every((key) => Object.prototype.hasOwnProperty.call(address, key) && address[key]);
  };

  const handlePaymentBtnClick = async () => {
    console.log("[TUNE] On payment button click, ORDER SESSION STORAGE:", paymentParameter);
    console.log("[TUNE] Customer", customer);
    console.log("[TUNE] Cart", cart);

    setIsLoading(true);

    // 주소 입력 했는지 & 필수 키에 대한 값이 전부 있는지 & 차단된 배송지인지 확인
    if (selectedAddress) {
      if (!isValidAddress(selectedAddress)) {
        openModal({
          title: "Shipping Address",
          description: i18next.t("toast.checkout.warn.checkout_invalid_shipping_address"),
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });
        setIsLoading(false);
        console.log(`${customer.id} failed address validation: `, selectedAddress);
        return;
      }

      const { zip, address1 } = selectedAddress;
      const isBlocked = blockedAddressList.current.some((blockedAddress) => {
        return (
          blockedAddress.zip === zip &&
          (blockedAddress.roadname_address.includes(address1) || blockedAddress.lotnumber_address.includes(address1))
        );
      });

      if (isBlocked) {
        openModal({
          title: "Blocked Address",
          description: i18next.t("toast.checkout.warn.checkout_shipping_address_blocked"),
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });
        setIsLoading(false);
        console.log(`${customer.id} failed address validation: `, selectedAddress);
        return;
      }
    } else {
      openToast(NOTIFICATIONS.WARN_CHECKOUT_SHIPPING_ADDRESS_EMPTY);
      setIsLoading(false);
      console.log(`${customer.id} failed address validation: `, selectedAddress);
      return;
    }
    if (!paymentParameter) {
      // TODO: 에러 message
      openModal({
        title: "Payment Error",
        description: NOTIFICATIONS.ERROR_PAYMENT_INFO.message,
        confirmButton: {
          onClick: () => {
            closeModal();
          },
        },
      });
      setIsLoading(false);
      console.log(`${customer.id} has no paymentParameter value`);
      return;
    }
    /**
     * TODO: 토스 페이먼츠 결제정보 추가
     * ex) customerName, customerEmail
     * https://docs.tosspayments.com/reference/js-sdk#requestpayment%EC%B9%B4%EB%93%9C-%EA%B2%B0%EC%A0%9C-%EC%A0%95%EB%B3%B4
     */
    const { data } = await checkCheckout();
    const { data: updatedUserInfo } = await fetchUser({
      variables: { customerAccessToken: accessToken },
    });

    const {
      id: cartId,
      buyerIdentity: {
        customer: { id, tags },
      },
      cost: {
        totalAmount: { amount },
      },
      lines,
      discountCodes,
    } = data.cart;

    // 재고 확인
    const quantityAdjustedCartLines = data?.cart.lines.edges.map(({ node }) => ({
      id: node.id,
      quantity:
        !node.merchandise.availableForSale || Number(node.quantity) > Number(node.merchandise.quantityAvailable)
          ? Number(node.merchandise.quantityAvailable)
          : Number(node.quantity),
    }));

    const isQuantityInsufficient = quantityAdjustedCartLines.some(
      (line, index) => line.quantity !== Number(data?.cart.lines.edges[index].node.quantity),
    );
    if (isQuantityInsufficient) {
      openModal({
        title: "Alert",
        description:
          "주문하시려는 상품의 수량이 주문 과정에서 구매 가능 수량을 초과하였습니다. 장바구니에서 해당 상품의 수량이 구매 가능 수량에 맞게 조정되며 품절된 상품은 제거됩니다.",
        confirmButton: {
          label: "Okay",
          onClick() {
            void updateCartLineBase({
              variables: { cartId: cartId ? cartId : cart.id, lines: quantityAdjustedCartLines },
            }).then(({ data }) => {
              if (!quantityAdjustedCartLines.some((node) => node.quantity > 0)) setIsEmpty(true);
              extractCart(data.cartLinesUpdate.cart);
              closeModal();
            });
          },
        },
      });
      setIsLoading(false);
      console.log(`${customer.id} has exceeded the available quantity for the product:`, quantityAdjustedCartLines);
      return;
    }

    // 체크아웃에 진입했을 때 개수제한 확인
    const quantityLimitMap = getQuantityLimitObj(updatedUserInfo);
    const adjustedCartLines = adjustCartLines(cart, quantityLimitMap);
    const isOverQuantity = !linesEqual(cart, adjustedCartLines);

    // 개수제한 못 맞췄을 때는 카트를 새로 생성
    if (isOverQuantity) {
      openModal({
        title: "Alert",
        description: "구매 가능한 수량을 초과했습니다.",
        confirmButton: {
          label: "Okay",
          onClick() {
            // capacity가 0인 상품만 존재할 경우
            if (adjustedCartLines.length === 0) setIsEmpty(true);

            void createCartWithParams({
              variables: {
                input: {
                  attributes: [{ key: "isFamilySale", value: "true" }],
                  buyerIdentity: { customerAccessToken: accessToken },
                  lines: adjustedCartLines,
                },
              },
            }).then(({ data }) => {
              const hasCartLineItems = data?.cartCreate.cart.lines.edges.length;
              if (hasCartLineItems) setIsEmpty(true);
              updateUser({ ...updatedUserInfo, accessToken });
              closeModal();
            });
          },
        },
      });
      setIsLoading(false);
      console.log(`${customer.id} has exceeded the available quantity for the product`);
      return;
    }

    // 직원일 경우 유효성검사 예외처리
    if (tags.includes("Employee")) {
      paymentMethod.updateAmount(payment?.totalAmount + payment?.shippingFee);
      setIsLoading(false);
      requestPayment({});
    } else {
      // 할인코드 유효성 검사
      if (payment.discountDescriptions.length > 0 && discountCodes.length > 0) {
        // discountCodes의 code가 멤버십 할인코드인지 확인
        const isMembershipCode = isCheckMembershipCode(discountCodes[0].code);

        // 멤버십 할인코드를 사용했는지 유무
        let usedMembershipDiscountCode = false;
        handlePaymentBtnClick;

        // 재사용 가능한 일반 할인코드를 사용했는지 유무
        let usedDiscountCode = false;

        let isAvailableDiscountCode = true;

        // 1. 멤버십 할인 코드인 경우, 멤버십 할인코드 사용내역 있는지 체크 (usedMembershipDiscountCode)
        if (isMembershipCode) {
          await fetchUser({ variables: { customerAccessToken: accessToken } }).then(({ data }) => {
            const validOrders = getValidOrders(data.customer.orders.edges);

            usedMembershipDiscountCode = validOrders.some(({ node }) =>
              node.discountApplications.edges.some(({ node: discountApplication }) => {
                // type이 `DiscountCodeApplication`인 경우, 쇼피파이 Discounts에서 생성된 할인코드를 의미함
                if (discountApplication.__typename === "DiscountCodeApplication") {
                  return isCheckMembershipCode(discountApplication.code);
                } else if (discountApplication.__typename === "ManualDiscountApplication") {
                  return isCheckMembershipCode(discountApplication.title);
                }
                return false;
              }),
            );
          });
        }

        // TODO: 재사용 가능한 할인코드의 네이밍 컨벤션 필요
        // 3. "MEMBER"란 이름의 할인코드인 경우 (주문취소 시, 재사용 가능한 코드)
        // if (discountCodes[0].code.toLowerCase().includes("member")) {
        //   await fetchUser({ variables: { customerAccessToken: accessToken } }).then(({ data }) => {
        //     const validOrders = getValidOrders(data.customer.orders.edges);
        //
        //     usedDiscountCode = validOrders.some(({ node }) =>
        //       node.discountApplications.edges.some(({ node: discountApplication }) => {
        //         if (discountApplication.__typename === "DiscountCodeApplication") {
        //           // `MEMBER`라는 이름의 할인코드가 유효한지 확인
        //           return discountApplication.code.toLowerCase().includes("member");
        //         }
        //         return false;
        //       }),
        //     );
        //   });
        // }

        // 2. 멤버십 할인코드를 사용한적이 없거나 멤버십 할인 코드가 아닌 경우, 해당 할인 코드 다시 적용 해서 유효성 검사하기
        if (!usedMembershipDiscountCode || !isMembershipCode || !usedDiscountCode) {
          const lines = cart.lines.edges.map((edge) => ({
            merchandiseId: edge.node.merchandise.id,
            quantity: edge.node.quantity,
          }));

          // 2-1. 할인코드 유효성 체크
          const { isAvailableDiscountCode } = await validateDiscountCode({
            accessToken,
            lines: lines,
            discountCodes: [discountCodes[0].code],
          });
        }

        // 3. 멤버십 할인코드를 사용한 적이 있으면 모달 노출
        if (usedMembershipDiscountCode || !isAvailableDiscountCode || usedDiscountCode) {
          openModal({
            title: "Alert",
            description: usedMembershipDiscountCode
              ? t("account.order.used_discount_alert")
              : t("account.order.expiration_alert"),
            confirmButton: {
              label: "Okay",
              onClick: async () => {
                const { data } = await removeCartDiscountCode({
                  variables: { cartId: cart.id, discountCodes: [] },
                });

                sessionStorage.removeItem("discount_code");

                const updateCartLines = data?.cartDiscountCodesUpdate.cart.lines.edges.map(({ node }) => ({
                  id: node.id,
                  attributes: [],
                }));

                const { data: cartData } = await updateCartLineBase({
                  variables: { cartId: cartId ? cartId : cart.id, lines: updateCartLines },
                });

                await extractCart(cartData?.cartLinesUpdate?.cart);
                console.log("Payment Cart", JSON.stringify(cartData));
                if (cartData?.cartLinesUpdate?.cart) updateCart(cartData?.cartLinesUpdate?.cart);

                closeModal();

                // 상태 업데이트 후 새로 고침
                setTimeout(() => {
                  location.reload();
                }, 200);
              },
            },
          });
          setIsLoading(false);
          console.log(`${customer.id} has used a membership discount code.`);
          return;
        }
      }

      isValidatePayment(id, discountCodes, lines, amount);
    }
  };

  const isValidatePayment = (id, discountCodes, lines, amount) => {
    // customer id가 이상한 경우
    if (extractId(customer.id) !== extractId(id)) {
      location.reload();
      setIsLoading(false);
      console.log(`${customer.id} customer ID does not match with the cart's customer ID.`);
      return;
    }

    // 결제 금액과 배송비 포함한 금액이 일치하는지 확인
    if (parseInt(amount) + payment?.shippingFee !== payment?.totalAmount + payment?.shippingFee) {
      location.reload();
      setIsLoading(false);
      console.log(
        `${customer.id} customer: The payment amount does not match the total amount including shipping fees.`,
      );
      return;
    }

    setIsLoading(false);
    paymentMethod.updateAmount(payment?.totalAmount + payment?.shippingFee);
    requestPayment({});
  };

  return (
    <div>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between">
          <span>{t("account.order.total")}</span>
          <span>
            {transformWon(payment?.subtotalAmount)}
            {t("account.order.currency")}
          </span>
        </div>

        <div className="flex justify-between">
          <span>{t("account.order.charge")}</span>
          <span>
            {payment?.shippingFee > 0
              ? transformWon(payment?.shippingFee) + t("account.order.currency")
              : t("account.order.free")}
          </span>
        </div>
        {payment?.subtotalAmount > payment?.totalAmount && (
          <div className="flex justify-between">
            <span>{t("account.order.discount")}</span>
            <span>
              {transformWon(payment?.totalAmount - payment?.subtotalAmount)}
              {t("account.order.currency")}
            </span>
          </div>
        )}
      </div>

      <p className="text-highlight mt-5 w-full">
        '배송 준비 중' 상태 이후 주문 취소, 교환, 반품이 불가하오니 신중한 구매 부탁드립니다.
      </p>

      <Button
        className="mt-2.5 h-9 w-full justify-between"
        onClick={handlePaymentBtnClick}
        isLoading={isLoading || discountLoading}
      >
        <span>{t("account.order.pay")}</span>
        <span>
          {transformWon(payment?.totalAmount + payment?.shippingFee)}
          {t("account.order.currency")}
        </span>
      </Button>
      {isProgressing ? <Loading description={t("account.order.wait_alert")} type="screen" /> : null}
    </div>
  );
}

export default Payment;
